import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { BankAccountsApiAdapter } from '../adapters/BankAccountsApiAdapter';
import type { BankAccountsFilters, BankAccountsMeta } from '../models/BankAccount';
import { BANK_ACCOUNTS_QUERY_KEY } from './useBankAccounts';

const DEFAULT_FILTERS: BankAccountsFilters = {};

/** La API puede devolver meta en snake_case o camelCase */
type ApiMeta = Partial<BankAccountsMeta> & {
  perPage?: number;
  currentPage?: number;
  lastPage?: number;
};

function normalizeBankAccountsMeta(meta: ApiMeta): BankAccountsMeta {
  return {
    total: meta.total ?? 0,
    per_page: meta.per_page ?? meta.perPage ?? 25,
    current_page: meta.current_page ?? meta.currentPage ?? 1,
    last_page: meta.last_page ?? meta.lastPage ?? 1,
  };
}

export const useBankAccountsInfinite = (filters?: BankAccountsFilters) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const apiAdapter = useMemo(() => new BankAccountsApiAdapter(httpClient), [httpClient]);
  const resolvedFilters = filters ?? DEFAULT_FILTERS;

  return useInfiniteQuery({
    queryKey: [BANK_ACCOUNTS_QUERY_KEY, 'infinite', resolvedFilters],
    queryFn: async ({ pageParam }) => {
      const response = await apiAdapter.getBankAccounts({
        ...resolvedFilters,
        page: pageParam as number,
        perPage: 25,
      });
      return {
        data: response.data,
        meta: normalizeBankAccountsMeta(response.meta as ApiMeta),
      };
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 60 * 1000,
  });
};
